import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Eye, EyeOff, ChevronRight } from 'lucide-react';
import { ProfileAvatar } from '../components/ProfileAvatar';

export default function LoginPage() {
  const [coachEmail, setCoachEmail] = useState('');
  const [coachPassword, setCoachPassword] = useState('');
  const [athleteEmail, setAthleteEmail] = useState('');
  const [athletePassword, setAthletePassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCoachPassword, setShowCoachPassword] = useState(false);
  const [showAthletePassword, setShowAthletePassword] = useState(false);
  const { loginCoach, loginAthlete, athleteProfiles, selectAthleteProfile } = useAuth();
  const navigate = useNavigate();

  const handleCoachLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginCoach(coachEmail, coachPassword);
      toast.success('Accesso effettuato con successo!');
      window.location.href = '/lessons';
    } catch (error) {
      toast.error(getErrorMessage(error, 'Credenziali non valide'));
      setLoading(false);
    }
  };

  const handleAthleteLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await loginAthlete(athleteEmail, athletePassword);
      if (result.profiles && result.profiles.length > 1) {
        // Mostra il selettore — non fare redirect
        setLoading(false);
        return;
      }
      toast.success('Accesso effettuato con successo!');
      window.location.href = '/athlete/dashboard';
    } catch (error) {
      toast.error(getErrorMessage(error, 'Credenziali non valide'));
      setLoading(false);
    }
  };

  const handleSelectProfile = async (profileId) => {
    setLoading(true);
    try {
      await selectAthleteProfile(profileId);
      toast.success('Accesso effettuato con successo!');
      window.location.href = '/athlete/dashboard';
    } catch (error) {
      toast.error('Errore nella selezione del profilo');
      setLoading(false);
    }
  };

  // Schermata selezione profilo (più figli stessa email)
  if (athleteProfiles) {
    return (
      <div className="min-h-[100svh] flex items-start sm:items-center justify-center bg-[#09090b] px-4 pt-12 pb-24 sm:py-0">
        <div className="max-w-md w-full space-y-6">
          <div className="text-center">
            <h1 className="text-3xl font-black text-white mb-1">Scegli il profilo</h1>
            <p className="text-zinc-400 text-sm">Seleziona il profilo con cui vuoi accedere</p>
          </div>
          <div className="space-y-3">
            {athleteProfiles.profiles.map((profile) => (
              <button
                key={profile.id}
                onClick={() => handleSelectProfile(profile.id)}
                disabled={loading}
                className="w-full glass-card border border-white/10 rounded-xl px-4 py-4 flex items-center gap-4 hover:border-[#ccff00]/50 hover:bg-zinc-900 transition-colors text-left"
              >
                <ProfileAvatar name={profile.name} photo={profile.profile_photo} size="md" />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold truncate">{profile.name}</p>
                  {profile.athlete_code && (
                    <p className="text-zinc-500 text-xs font-mono mt-0.5">Codice: {profile.athlete_code}</p>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-500 flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100svh] flex items-start sm:items-center justify-center bg-[#09090b] px-4 sm:px-6 lg:px-8 pt-8 pb-24 sm:py-0 overflow-y-auto">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="font-black tracking-tight">
            <span className="block text-5xl sm:text-6xl lg:text-7xl text-[#ccff00]">
              Trainers'
            </span>
            <span className="block text-2xl sm:text-3xl lg:text-4xl text-white manager-word">
              MANAGER
            </span>
          </h1>
          <p className="text-zinc-400">Accedi al tuo account</p>
        </div>

        <Tabs defaultValue="coach" className="glass-card p-8 rounded-sm">
          <TabsList className="grid w-full grid-cols-2 bg-zinc-900/50 mb-6">
            <TabsTrigger value="coach" className="data-[state=active]:bg-[#ccff00] data-[state=active]:text-black">
              Allenatore
            </TabsTrigger>
            <TabsTrigger value="athlete" className="data-[state=active]:bg-[#ccff00] data-[state=active]:text-black">
              Atleta
            </TabsTrigger>
          </TabsList>

          <TabsContent value="coach">
            <form onSubmit={handleCoachLogin} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="coach-email" className="text-zinc-300">Email</Label>
                <Input
                  id="coach-email"
                  data-testid="login-coach-email-input"
                  type="email"
                  value={coachEmail}
                  onChange={(e) => setCoachEmail(e.target.value)}
                  placeholder="il-tuo-email@esempio.it"
                  className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-1 focus:ring-[#ccff00] rounded-sm h-12 text-lg"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="coach-password" className="text-zinc-300">Password</Label>
                <div className="relative">
                  <Input
                    id="coach-password"
                    data-testid="login-coach-password-input"
                    type={showCoachPassword ? "text" : "password"}
                    value={coachPassword}
                    onChange={(e) => setCoachPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-1 focus:ring-[#ccff00] rounded-xl h-12 text-lg pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowCoachPassword(!showCoachPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                  >
                    {showCoachPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                data-testid="login-coach-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold uppercase tracking-wide rounded-sm h-12 shadow-[0_0_15px_rgba(204,255,0,0.3)] hover:scale-[1.02] transition-all"
              >
                {loading ? 'Accesso...' : 'Accedi come Allenatore'}
              </Button>

              <div className="text-center text-sm text-zinc-400">
                <Link to="/forgot-password" className="text-zinc-500 hover:text-[#ccff00] transition-colors">
                  Password dimenticata?
                </Link>
              </div>

              <div className="text-center text-sm text-zinc-400">
                Non hai un account?{' '}
                <Link to="/register" className="text-[#ccff00] hover:underline font-medium">
                  Registrati
                </Link>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="athlete">
            <form onSubmit={handleAthleteLogin} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="athlete-email" className="text-zinc-300">Email</Label>
                <Input
                  id="athlete-email"
                  data-testid="login-athlete-email-input"
                  type="email"
                  value={athleteEmail}
                  onChange={(e) => setAthleteEmail(e.target.value)}
                  placeholder="il-tuo-email@esempio.it"
                  className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-1 focus:ring-[#ccff00] rounded-sm h-12 text-lg"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="athlete-password" className="text-zinc-300">Password</Label>
                <div className="relative">
                  <Input
                    id="athlete-password"
                    data-testid="login-athlete-password-input"
                    type={showAthletePassword ? "text" : "password"}
                    value={athletePassword}
                    onChange={(e) => setAthletePassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-1 focus:ring-[#ccff00] rounded-xl h-12 text-lg pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowAthletePassword(!showAthletePassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                  >
                    {showAthletePassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                data-testid="login-athlete-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold uppercase tracking-wide rounded-sm h-12 shadow-[0_0_15px_rgba(204,255,0,0.3)] hover:scale-[1.02] transition-all"
              >
                {loading ? 'Accesso...' : 'Accedi come Atleta'}
              </Button>

              <div className="text-center text-sm text-zinc-400">
                <Link to="/forgot-password" className="text-zinc-500 hover:text-[#ccff00] transition-colors">
                  Password dimenticata?
                </Link>
              </div>

              <div className="text-center text-sm text-zinc-400">
                Non hai un account?{' '}
                <Link to="/athlete/register" className="text-[#ccff00] hover:underline font-medium">
                  Registrati
                </Link>
              </div>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
